/**
 * Zustand undo store — reactive wrapper around ActionUndoManager.
 *
 * Provides React-subscribable state for:
 * - canUndo / canRedo booleans
 * - lastDescription for toast display
 * - undo() / redo() / push() / clear() actions
 *
 * Non-React access via useUndoStore.getState() for keyboard handlers.
 */

import { create } from "zustand"
import { ActionUndoManager, type UndoEntry } from "./ActionUndoManager"
import { showUndoToast } from "./undoToast"

// ---------------------------------------------------------------------------
// State + Actions
// ---------------------------------------------------------------------------

interface UndoState {
  canUndo: boolean
  canRedo: boolean
  undoCount: number
  redoCount: number
  /** Description of the most recent undoable action */
  lastDescription: string | null
}

interface UndoActions {
  /** Push a new undoable entry and sync state */
  push: (entry: UndoEntry) => void
  /** Execute undo and sync state. Returns the entry if successful. */
  undo: () => Promise<UndoEntry | null>
  /** Execute redo and sync state. Returns the entry if successful. */
  redo: () => Promise<UndoEntry | null>
  /** Clear all undo/redo history */
  clear: () => void
}

// ---------------------------------------------------------------------------
// Singleton manager instance (private to this module)
// ---------------------------------------------------------------------------

const manager = new ActionUndoManager()

/** Sync Zustand state from the manager's current state */
function syncState(): Partial<UndoState> {
  return {
    canUndo: manager.canUndo,
    canRedo: manager.canRedo,
    undoCount: manager.undoCount,
    redoCount: manager.redoCount,
    lastDescription: manager.lastAction?.description ?? null,
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useUndoStore = create<UndoState & UndoActions>((set) => ({
  // Initial state
  canUndo: false,
  canRedo: false,
  undoCount: 0,
  redoCount: 0,
  lastDescription: null,

  // Actions
  push: (entry) => {
    manager.push(entry)
    set(syncState())
    // Show undo toast for all operations that have a description.
    // Reorder operations don't register undo at all, so they never reach here.
    if (entry.description) {
      showUndoToast(entry.description)
    }
  },

  undo: async () => {
    const entry = await manager.undo()
    set(syncState())
    return entry
  },

  redo: async () => {
    const entry = await manager.redo()
    set(syncState())
    return entry
  },

  clear: () => {
    manager.clear()
    set(syncState())
  },
}))

// ---------------------------------------------------------------------------
// Convenience: push undo entry (for use in operations layer)
// ---------------------------------------------------------------------------

/**
 * Register an undoable action. Call this AFTER performing the mutation.
 *
 * @param description - Human-readable description (for toast)
 * @param undo - Function to reverse the action
 * @param redo - Function to re-apply the action
 * @param type - Category ('create' | 'update' | 'delete' | 'batch')
 *
 * @example
 * ```ts
 * await db.totodo.update(id, { status: ObjectStatus.Completed })
 * registerUndo("Completed task", () => db.totodo.update(id, { status: prev }), () => db.totodo.update(id, { status: ObjectStatus.Completed }))
 * ```
 */
export function registerUndo(
  description: string,
  undo: () => Promise<void>,
  redo: () => Promise<void>,
  type: UndoEntry["type"] = "update",
): void {
  useUndoStore.getState().push({
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    description,
    type,
    undo,
    redo,
  })
}
